import { print } from 'graphql';
import { gql } from 'graphql-tag';
import type { RxCollection, RxDocument, RxJsonSchema } from 'rxdb';

import { AdditionalFields } from '../../models/models';
import { getDateModified, getRoleForMutation } from '../../utilities/utilityFunctions';
import { defaultDoc, getValueFromLocalStorage } from '../share/defaultDoc';

export interface ComponentChecklist {
  readonly inspectionId?: number;
  readonly pk_AssetInspectionComponentChecklist?: string;
  readonly pk_uuid: string;
  readonly isDelete: string;
  readonly assetCategoryComponent: number;
  readonly fk_Component: number;
  readonly checkListComponentName: string;
  readonly discipline?: number;
  readonly site?: number;
  readonly dateModified?: string;
  readonly dateCreated?: string;
  readonly inspectionTaskId?: number;
  readonly fkInspectorField?: number;
  readonly fkInspectorLead?: number;
  isChanged?: boolean;
  checked?: boolean;
  sort?: number;
}

export interface ComponentChecklistAdditionalFields extends AdditionalFields {
  pk_AssetInspectionComponentChecklist?: string;
  fkInspectorField?: number;
  fkInspectorLead?: number;
}

export const componentChecklistSchema: RxJsonSchema<ComponentChecklist> = {
  type: 'object',
  version: 0,
  description: 'describes deficiency lists',
  primaryKey: 'pk_uuid',
  properties: {
    pk_uuid: { type: 'string', maxLength: 36 },
    pk_AssetInspectionComponentChecklist: { type: ['string', 'null'] },
    assetCategoryComponent: { type: ['number', 'null'] },
    fk_Component: { type: ['number', 'null'] },
    checkListComponentName: { type: ['string', 'null'] },
    inspectionId: { type: ['number', 'null'] },
    isDelete: { type: ['string', 'null'] },
    inspectionTaskId: { type: ['number', 'null'] },
    fkInspectorField: { type: ['number', 'null'] },
    fkInspectorLead: { type: ['number', 'null'] },
    discipline: { type: ['number', 'null'] },
    site: { type: ['number', 'null'] },
    dateCreated: { type: ['string', 'null'] },
    dateModified: { type: ['string', 'null'] },
  },
};

export type ComponentChecklistCollection = RxCollection<ComponentChecklist> | null;

export type ComponentChecklistDocument = RxDocument<ComponentChecklist>;

export const componentChecklistPullQueryBuilder = (document: ComponentChecklist) => {
  let doc = document;
  if (!doc) {
    (doc as unknown) = { ...defaultDoc };
  }

  const query = print(gql`
    query componentChecklistFeed($siteId: Int!, $disciplineId: Int!, $lastRequested: DateTime!) {
      componentChecklistFeed(
        siteId: $siteId
        disciplineId: $disciplineId
        lastRequested: $lastRequested
      ) {
        inspectionId
        pk_AssetInspectionComponentChecklist
        pk_uuid
        isDelete
        assetCategoryComponent
        fk_Component
        checkListComponentName
        site
        discipline
        dateCreated
        dateModified
        inspectionTaskId
        fkInspectorField
        fkInspectorLead
      }
    }
  `);

  return {
    query,
    variables: {
      siteId: JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
      disciplineId: JSON.parse(getValueFromLocalStorage('selectedDiscipline'))
        ?.selectedDisciplineId,
      lastRequested: getDateModified(
        doc?.dateModified,
        doc?.dateCreated,
        doc.site,
        doc.discipline,
        JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
        JSON.parse(getValueFromLocalStorage('selectedDiscipline'))?.selectedDisciplineId,
      ),
    },
  };
};

export const componentChecklistPushQueryBuilder = (doc: ComponentChecklistDocument[]) => {
  const document = doc[0];
  const query = print(gql`
    mutation saveDeleteComponentCheckList(
      $role: Int!
      $MobileAssetInspectionComponentChecklistInfoInput: MobileAssetInspectionComponentChecklistInfoInput
    ) {
      saveDeleteComponentCheckList(
        role: $role
        componentsChecklist: $MobileAssetInspectionComponentChecklistInfoInput
      ) {
        pk_AssetInspectionComponentChecklist
        pk_uuid
        status
      }
    }
  `);

  const role = getRoleForMutation(document);

  const variables = {
    role,
    MobileAssetInspectionComponentChecklistInfoInput: document,
  };
  return {
    query,
    variables,
  };
};

export const componentChecklistPushModifier = (doc: ComponentChecklistDocument) => {
  const obj: ComponentChecklistDocument & ComponentChecklistAdditionalFields = {
    ...doc,
  };
  if (obj.pk_AssetInspectionComponentChecklist === '0') {
    obj.pk_AssetInspectionComponentChecklist = undefined;
  }
  const blackListedProp = [
    '_attachments',
    '_rev',
    '_deleted',
    'dateCreated',
    'dateModified',
    '_meta',
    'isDeleted',
  ];
  blackListedProp.forEach(property => {
    obj[property as keyof ComponentChecklistAdditionalFields] = undefined;
  });
  return obj;
};

export default {};
